import { LocalVideoTrack } from 'livekit-client'
import { BgEffect } from '@/src/lib/types'
import { ProcessorWrapper, BackgroundTransformer } from '@/src/processor'
import { BlurSwitcher } from '@/src/components/controls/BlurSwitcher'
import { ImgSwitcher } from '@/src/components/controls/ImgSwitcher'
import { parsBrandingDataDefault } from '@/src/lib/types'
import { CloseIcon } from '@/accets/icons'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IBranding } from '@/src/types/meetData.type'
import { initialBranding } from '@/src/constants/branding'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'
import { useToggleTrackProcessor } from '@/src/hooks/room/track/useToggleTrackProcessor'

export interface VirtualBgHandler extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  track: LocalVideoTrack | undefined
  isPreJoin: boolean
  changeBgEffect: (effect: BgEffect | null) => React.MouseEventHandler<HTMLButtonElement> | void
}

export function VirtualBgHandler({ track, isPreJoin = true, changeBgEffect }: VirtualBgHandler) {
  const { t } = useTranslation()

  const {
    text_primary_color,
    border_radius: borderRadius,
    img_background_speaker,
  } = useAppSelector(selectBranding)
  const [currentBgEffect, setCurrentBgEffect] = useState<BgEffect | null>(
    JSON.parse(localStorage.getItem('lk-user-choices') || '{}')?.currentBgEffect,
  )
  const [userChoices, setUserChoices] = useState<object>(
    JSON.parse(localStorage.getItem('lk-user-choices') || '{}'),
  )

  useEffect(() => {
    setUserChoices(JSON.parse(localStorage.getItem('lk-user-choices') || '{}'))
  }, [])

  useEffect(() => {
    const setProcessor = async () => {
      await useToggleTrackProcessor(track, currentBgEffect)
    }
    if (track) {
      setProcessor()
    }
  }, [currentBgEffect])

  useEffect(() => {
    const setProcessor = async () => {
      await useToggleTrackProcessor(track, currentBgEffect)
    }
    if (track && isPreJoin) {
      setProcessor()
    }
  }, [track])

  useEffect(() => {
    if (userChoices) {
      const newUserChoices = {
        ...userChoices,
        currentBgEffect: currentBgEffect,
      }
      localStorage.setItem('lk-user-choices', JSON.stringify(newUserChoices))
      window.dispatchEvent(new Event('storage'))
    }
    changeBgEffect(currentBgEffect)
  }, [currentBgEffect])

  const isProcessorSupported = useMemo(
    () => ProcessorWrapper.isSupported && BackgroundTransformer.isSupported,
    [],
  )

  const handleBgEffect = useCallback((type: BgEffect) => {
    setCurrentBgEffect(type)
  }, [])

  return (
    isProcessorSupported && (
      <div className="lk-bg-effects">
        <div className="lk-bg-effects-title">
          <p
            style={{
              color: text_primary_color,
            }}
          >
            {t('changeBg')}:
          </p>
        </div>

        <button
          aria-label="Delete blur effect"
          className={[
            'lk-button-bg-effects',
            currentBgEffect?.type === 'none' || !currentBgEffect ? 'active' : '',
          ].join(' ')}
          style={{
            borderColor:
              currentBgEffect?.type === 'none' || !currentBgEffect
                ? text_primary_color
                : 'transparent',
            borderRadius: borderRadius,
          }}
          onClick={() => setCurrentBgEffect({ type: 'none', options: {} })}
        >
          <CloseIcon stroke="red" fill={text_primary_color} width={36} height={36} />
        </button>

        <BlurSwitcher
          currentBgEffect={currentBgEffect}
          track={track}
          setBgEffect={handleBgEffect}
        />

        {img_background_speaker && (
          <ImgSwitcher
            currentBgEffect={currentBgEffect}
            track={track}
            setBgEffect={handleBgEffect}
          />
        )}
      </div>
    )
  )
}
